import React, { useCallback, useEffect, useState } from 'react'
import { Card, Space, Button, Image, Popconfirm } from 'antd'
import Table, { type ColumnsType } from 'antd/es/table'
import { type AxiosError } from 'axios'
import { setLoader } from 'Store/slices/commonSlice'
import { useAppDispatch } from 'Hooks/ReduxHook'
import { DeleteOutlined, EditOutlined, PlusOutlined } from '@ant-design/icons'
import { toast } from 'sonner'
import Http, { type ApiErrorData } from 'Util/Http'
import { type IFeed } from 'Util/Interface'
import { ATTCHMENT_BASE_URL } from 'Util/Constant'
import { useNavigate } from 'react-router-dom'
import dayjs from 'dayjs'

const Feed: React.FC = () => {
  const [tableData, setTableData] = useState<IFeed[]>([])

  const dispatch = useAppDispatch()
  const navigate = useNavigate()
  const fetch = useCallback(async () => {
    try {
      dispatch(setLoader(true))
      const response = await Http(`/feed`)
      if (response.data?.status && response?.data?.code === 200) {
        dispatch(setLoader(false))
        setTableData(response.data?.data)
      } else {
        setTableData([])
        dispatch(setLoader(false))
      }
    } catch (error: any) {
      const err = error as AxiosError<ApiErrorData>
      toast.error(err.response?.data?.message ?? 'Something Went Wrong')
      setTableData([])
      dispatch(setLoader(false))
    }
  }, [dispatch])

  useEffect(() => {
    void fetch()
  }, [fetch])

  const handleDelete = async (params: IFeed) => {
    try {
      let event = params
      try {
        const detail = await Http(`/feed/${String(params.id)}`)
        if (detail.data?.status && detail.data?.code === 200 && detail.data?.data) {
          event = detail.data.data as IFeed
        }
      } catch {
        // use row data if detail fetch fails
      }

      const formData = new FormData()
      formData.append('id', String(event.id))
      formData.append('eventId', String(event.id))
      formData.append('recordId', String(event.id))
      formData.append('eventName', event.functionName)
      formData.append('functionName', event.functionName)
      formData.append('eventCity', event.functionCity)
      formData.append('functionCity', event.functionCity)
      formData.append('eventDetails', event.functionDetails)
      formData.append('functionDetails', event.functionDetails)
      formData.append('eventType', event.eventType ?? 'event')
      const formattedDate = dayjs(event.functionDate).format('YYYY/MM/DD')
      formData.append('eventDate', formattedDate)
      formData.append('functionDate', formattedDate)

      const response = await Http({
        url: '/feed/remove-event',
        method: 'POST',
        data: formData,
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      })

      if (response.data?.status && response?.data?.code === 200) {
        toast.success(response?.data?.message ?? 'Event deleted successfully')
        void fetch()
        return
      }

      toast.error(response?.data?.message ?? 'Failed to delete event')
    } catch (error) {
      const err = error as AxiosError<ApiErrorData>
      toast.error(err.response?.data?.message ?? 'Something Went Wrong')
    }
  }

  const columns: ColumnsType<IFeed> = [
    {
      title: 'Id',
      dataIndex: 'index',
      key: 'id',
      render: (value, item, index) => (1 - 1) * 10 + index + 1,
    },
    {
      title: 'Function Name',
      dataIndex: 'functionName',
      key: 'functionName',
    },
    {
      title: 'Function Details',
      dataIndex: 'functionDetails',
      key: 'functionDetails',
    },
    {
      title: 'Function Date',
      dataIndex: 'functionDate',
      key: 'functionDate',
    },
    {
      title: 'Function City',
      dataIndex: 'functionCity',
      key: 'functionCity',
    },
    {
      title: 'Image1',
      dataIndex: 'functionImage1',
      key: 'functionImage1',
      render: (value) => {
        return (
          <>
            {value ? (
              <Image src={`${ATTCHMENT_BASE_URL}${String(value)}`} alt="profile" height={100} width={100} />
            ) : (
              <div style={{ background: 'grey', height: 100, width: 100 }}></div>
            )}
          </>
        )
      },
    },
    {
      title: 'Image2',
      dataIndex: 'functionImage2',
      key: 'functionImage2',
      render: (value) => {
        return (
          <>
            {value ? (
              <Image src={`${ATTCHMENT_BASE_URL}${String(value)}`} alt="profile" height={100} width={100} />
            ) : (
              <div style={{ background: 'grey', height: 100, width: 100 }}></div>
            )}
          </>
        )
      },
    },
    {
      title: 'Image3',
      dataIndex: 'functionImage3',
      key: 'functionImage3',
      render: (value) => {
        return (
          <>
            {value ? (
              <Image src={`${ATTCHMENT_BASE_URL}${String(value)}`} alt="profile" height={100} width={100} />
            ) : (
              <div style={{ background: 'grey', height: 100, width: 100 }}></div>
            )}
          </>
        )
      },
    },
    {
      title: 'Image4',
      dataIndex: 'functionImage4',
      key: 'functionImage4',
      render: (value) => {
        return (
          <>
            {value ? (
              <Image src={`${ATTCHMENT_BASE_URL}${String(value)}`} alt="profile" height={100} width={100} />
            ) : (
              <div style={{ background: 'grey', height: 100, width: 100 }}></div>
            )}
          </>
        )
      },
    },
    {
      title: 'Image5',
      dataIndex: 'functionImage5',
      key: 'functionImage5',
      render: (value) => {
        return (
          <>
            {value ? (
              <Image src={`${ATTCHMENT_BASE_URL}${String(value)}`} alt="profile" height={100} width={100} />
            ) : (
              <div style={{ background: 'grey', height: 100, width: 100 }}></div>
            )}
          </>
        )
      },
    },
    {
      title: 'Action',
      dataIndex: 'actions',
      width: '15%',
      render: (_text, record: IFeed) => {
        return (
          <Space>
            <Button
              icon={<EditOutlined />}
              onClick={() => {
                navigate(`/feed/${record.id}`)
              }}
            >
              Update
            </Button>
            <Popconfirm
              title="Delete this event?"
              description="This action cannot be undone."
              okText="Delete"
              cancelText="Cancel"
              okButtonProps={{ danger: true }}
              onConfirm={() => {
                void handleDelete(record)
              }}
            >
              <Button danger icon={<DeleteOutlined />}>
                Delete
              </Button>
            </Popconfirm>
          </Space>
        )
      },
    },
  ]

  return (
    <>
      <div className="content-wrapper">
        <Card
          className="card-wrapper"
          title={
            <div className="row justify-between">
              <div>
                <h3>Event List</h3>
              </div>
              <div>
                <Button
                  type="primary"
                  icon={<PlusOutlined />}
                  onClick={() => {
                    navigate('/feed/add')
                  }}
                >
                  Add Feed
                </Button>
              </div>
            </div>
          }
          style={{ padding: '1.25rem 1.25rem 0' }}
        >
          <Table columns={columns} dataSource={tableData} rowKey={(dataIndex: IFeed) => dataIndex?.id} scroll={{ x: 800 }} bordered size="middle" />
        </Card>
      </div>
    </>
  )
}

export default Feed
